import React from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import {
  Home, FileText, AlertCircle, Gift, Users, DollarSign,
  Building2, Menu, LogOut, Settings, Bell, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BuyerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const builderData = JSON.parse(localStorage.getItem('builderData') || '{}');
  
  const navItems = [
    { icon: Home, label: 'Dashboard', href: '/buyer' },
    { icon: FileText, label: 'Documents', href: '/buyer/documents' },
    { icon: AlertCircle, label: 'Complaints', href: '/buyer/complaints' },
    { icon: Gift, label: 'Referrals', href: '/buyer/referrals' },
    { icon: Users, label: 'Rent Management', href: '/buyer/rent' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('buyerToken');
    localStorage.removeItem('buyerData');
    localStorage.removeItem('builderData');
    navigate('/buyer/login');
  };

  const isActive = (href) => {
    if (href === '/buyer') return location.pathname === '/buyer';
    return location.pathname.startsWith(href);
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-amber-600" />
          <div>
            <h2 className="font-bold text-lg">{builderData?.name || 'Builder Portal'}</h2>
            <p className="text-xs text-muted-foreground">Buyer Portal</p>
          </div>
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive(item.href)
                ? 'bg-amber-100 text-amber-900 font-medium'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-amber-100 text-amber-700">
              {buyerData?.name?.charAt(0) || 'B'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{buyerData?.name}</p>
            <p className="text-xs text-muted-foreground">Unit {buyerData?.unit_number}</p>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3">
          <Badge variant="outline" className="text-amber-600 border-amber-200">
            {buyerData?.loyalty_tier?.toUpperCase() || 'BRONZE'}
          </Badge>
          <span className="text-sm font-medium">{buyerData?.loyalty_points || 0} pts</span>
        </div>
        <Button variant="outline" className="w-full" onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b z-40 flex items-center px-4">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <NavContent />
          </SheetContent>
        </Sheet>
        <div className="flex-1 flex items-center justify-center">
          <span className="font-semibold">{builderData?.name || 'Buyer Portal'}</span>
        </div>
        <Button variant="ghost" size="icon">
          <Bell className="h-5 w-5" />
        </Button>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r z-40 flex-col">
        <NavContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
